import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { generateClient } from 'aws-amplify/data'
import { listAll } from '@utils/listAll'
import type { Schema } from '../../amplify/data/resource'

const client = generateClient<Schema>()

export const useRolesStore = defineStore('roles', () => {
	// State
	const roles = ref<any[]>([])
	const loading = ref(false)
	const error = ref<string | null>(null)
	const lastFetched = ref<Date | null>(null)
	const cacheTimeout = 5 * 60 * 1000 // 5 minutes

	// Computed
	const isStale = computed(() => {
		if (!lastFetched.value) return true
		return Date.now() - lastFetched.value.getTime() > cacheTimeout
	})

	const activeRoles = computed(() => 
		roles.value.filter(role => role.active !== false)
	)

	const adminRoles = computed(() => 
		roles.value.filter(role => role.isAdmin === true)
	)

	// Actions
	const fetchRoles = async (force = false) => {
		if (!force && !isStale.value && roles.value.length > 0) {
			return roles.value
		}

		loading.value = true
		error.value = null

		try {
			// Name-ascending from the same GSI the Roles table pages through. Read every page — a bare
			// `list()` returns only the first, and a dropdown silently missing a role is
			// indistinguishable from that role not existing.
			//
			// That the roles dropdown is ordered at all is a DELIBERATE DIVERGENCE from gen1, which
			// ordered the user-form's facility and groups lists but left roles in scan order (mcr
			// ModalEditUser.ts:207-226). Two alphabetical dropdowns beside a scrambled third reads as
			// a bug.
			roles.value = await listAll(({ limit, nextToken }) =>
				client.models.Role.listRoleByModelNameAndNameKey(
					{ modelName: 'Role' },
					{ sortDirection: 'ASC', limit, nextToken },
				))
			lastFetched.value = new Date()
			
			return roles.value
		} catch (err) {
			console.error('Error fetching roles:', err)
			error.value = err instanceof Error ? err.message : 'Failed to fetch roles'
			throw err
		} finally {
			loading.value = false
		}
	}

	const invalidateCache = () => {
		lastFetched.value = null
		roles.value = []
	}

	const refreshCache = async () => {
		return await fetchRoles(true)
	}

	return {
		// State
		roles,
		loading,
		error,
		lastFetched,

		// Computed
		isStale,
		activeRoles,
		adminRoles,

		// Actions
		fetchRoles,
		invalidateCache,
		refreshCache
	}
})
